import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { getCourses, createCourse, updateCourse, deleteCourse, uploadCourseImage } from '../../utils/api';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import CourseStateBadge from '../../components/CourseStateBadge';
import { Switch } from '../../components/ui/switch';
import { Plus, Edit, Trash2, BookOpen, Upload, Image, DollarSign, Video, Link2, Webhook, HelpCircle, Share2, Copy, Facebook, Twitter, Linkedin, Mail } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../../components/ui/popover';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const DEFAULT_PAYMENT_METHODS = {
  paypal: { enabled: false, link: '' },
  sinpe: { enabled: false, number: '', name: '' },
  nequi: { enabled: false, number: '' },
  daviplata: { enabled: false, number: '' },
  mercadopago: { enabled: false, link: '' },
  yappy: { enabled: false, number: '' },
  bank_transfer: { enabled: false, details: '' },
  offline: { enabled: false, details: '' },
  other: { enabled: false, name: '', details: '' },
};

const PAYMENT_LABELS = {
  paypal: 'PayPal',
  sinpe: 'SINPE Móvil (Costa Rica)',
  nequi: 'Nequi (Colombia)',
  daviplata: 'Daviplata (Colombia)',
  mercadopago: 'Mercado Pago',
  yappy: 'Yappy (Panamá)',
  bank_transfer: 'Transferencia Bancaria',
  offline: 'Pago Offline (efectivo / oficina)',
  other: 'Otro método',
};

const emptyForm = () => ({
  title: '', slug: '', description: '', requirements: '',
  hours: '', imageUrl: '', status: 'draft',
  isFree: true, price: '',
  deliveryMode: 'online', zoomLink: '',
  paymentMethods: JSON.parse(JSON.stringify(DEFAULT_PAYMENT_METHODS)),
  scholarshipsEnabled: false, scholarshipsMax: 0,
  startDate: '', endDate: '',
  enrollmentFormEnabled: false, webhookUrl: '',
  instructorWhatsapp: '', instructorWhatsappMessage: '',
});

// Help icon with rich content popover
const HelpIcon = ({ title, children, testId }) => (
  <Popover>
    <PopoverTrigger asChild>
      <button
        type="button"
        className="inline-flex items-center justify-center w-5 h-5 rounded-full text-muted-foreground hover:text-primary hover:bg-secondary transition-colors"
        data-testid={testId}
        aria-label="Ayuda"
      >
        <HelpCircle className="w-4 h-4" />
      </button>
    </PopoverTrigger>
    <PopoverContent className="w-80 text-sm" side="right" align="start">
      <p className="font-semibold text-primary mb-2">{title}</p>
      <div className="text-xs text-muted-foreground space-y-1.5 leading-relaxed">{children}</div>
    </PopoverContent>
  </Popover>
);

// Public share dropdown for a course
const ShareCourseButton = ({ course }) => {
  const publicUrl = `${window.location.origin}/courses/${course.id}`;
  const enrollUrl = `${window.location.origin}/enroll/${course.id}`;
  const text = encodeURIComponent(`Curso: ${course.title} — IAASP Academy`);
  const url = encodeURIComponent(publicUrl);

  const copyToClipboard = (val, label) => {
    navigator.clipboard.writeText(val).then(() => toast.success(`${label} copiado`));
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button size="sm" variant="outline" data-testid={`share-course-${course.id}`}>
          <Share2 className="w-4 h-4" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-80" align="end">
        <p className="font-semibold text-sm mb-2">Compartir este curso</p>
        <p className="text-xs text-muted-foreground mb-3">
          Pega este enlace en redes sociales, WhatsApp o email.
        </p>

        <div className="space-y-2 mb-3">
          <div>
            <p className="text-xs font-semibold text-muted-foreground mb-1">URL pública del curso</p>
            <div className="flex gap-1">
              <input readOnly value={publicUrl} className="flex-1 text-xs font-mono px-2 py-1.5 border rounded-lg bg-secondary" onClick={(e) => e.target.select()} />
              <Button type="button" size="sm" variant="outline" onClick={() => copyToClipboard(publicUrl, 'URL del curso')} data-testid={`copy-course-url-${course.id}`}>
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>

          {course.enrollment_form_enabled && (
            <div>
              <p className="text-xs font-semibold text-muted-foreground mb-1">URL del formulario de inscripción</p>
              <div className="flex gap-1">
                <input readOnly value={enrollUrl} className="flex-1 text-xs font-mono px-2 py-1.5 border rounded-lg bg-secondary" onClick={(e) => e.target.select()} />
                <Button type="button" size="sm" variant="outline" onClick={() => copyToClipboard(enrollUrl, 'URL de inscripción')} data-testid={`copy-enroll-url-${course.id}`}>
                  <Copy className="w-3.5 h-3.5" />
                </Button>
              </div>
            </div>
          )}
        </div>

        <p className="text-xs font-semibold text-muted-foreground mb-2">Compartir en redes</p>
        <div className="grid grid-cols-4 gap-2">
          <a href={`https://www.facebook.com/sharer/sharer.php?u=${url}`} target="_blank" rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary transition-colors text-xs"
            data-testid={`share-fb-${course.id}`}>
            <Facebook className="w-5 h-5 text-blue-600" />
            <span>Facebook</span>
          </a>
          <a href={`https://twitter.com/intent/tweet?url=${url}&text=${text}`} target="_blank" rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary transition-colors text-xs"
            data-testid={`share-tw-${course.id}`}>
            <Twitter className="w-5 h-5 text-sky-500" />
            <span>Twitter</span>
          </a>
          <a href={`https://www.linkedin.com/sharing/share-offsite/?url=${url}`} target="_blank" rel="noopener noreferrer"
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary transition-colors text-xs"
            data-testid={`share-ln-${course.id}`}>
            <Linkedin className="w-5 h-5 text-blue-700" />
            <span>LinkedIn</span>
          </a>
          <a href={`mailto:?subject=${text}&body=${url}`}
            className="flex flex-col items-center gap-1 p-2 rounded-lg hover:bg-secondary transition-colors text-xs"
            data-testid={`share-email-${course.id}`}>
            <Mail className="w-5 h-5 text-gray-600" />
            <span>Email</span>
          </a>
        </div>
        <a href={`https://wa.me/?text=${text}%20${url}`} target="_blank" rel="noopener noreferrer"
          className="mt-2 block text-center text-xs font-semibold text-green-600 hover:underline"
          data-testid={`share-wa-${course.id}`}>
          Compartir por WhatsApp
        </a>
      </PopoverContent>
    </Popover>
  );
};

export const ManageCourses = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingCourse, setEditingCourse] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [imageUploading, setImageUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState(null);
  const [tempImageFile, setTempImageFile] = useState(null);
  const imageRef = useRef();

  useEffect(() => {
    if (user && !['superadmin', 'admin', 'professor'].includes(user.role)) {
      navigate('/dashboard');
      return;
    }
    fetchCourses();
  }, [user, navigate]);

  const fetchCourses = async () => {
    try {
      const response = await getCourses();
      const all = response.data;
      setCourses(user?.role === 'professor' ? all.filter(c => c.instructor_id === user.id) : all);
    } catch (error) {
      toast.error('Error al cargar cursos');
    } finally {
      setLoading(false);
    }
  };

  const setField = (key, value) => setForm(p => ({ ...p, [key]: value }));
  const setPayment = (method, field, value) =>
    setForm(p => ({
      ...p,
      paymentMethods: {
        ...p.paymentMethods,
        [method]: { ...p.paymentMethods[method], [field]: value },
      },
    }));

  const resetForm = () => { setForm(emptyForm()); setEditingCourse(null); setImagePreview(null); setTempImageFile(null); };

  const handleImagePick = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setTempImageFile(file);
    setImagePreview(URL.createObjectURL(file));
  };

  const handleEdit = (course) => {
    setEditingCourse(course);
    setForm({
      title: course.title || '',
      slug: course.slug || '',
      description: course.description || '',
      requirements: course.requirements || '',
      hours: course.hours?.toString() || '',
      imageUrl: course.image_url || '',
      status: course.status || 'draft',
      isFree: course.is_free !== false,
      price: course.price?.toString() || '',
      deliveryMode: course.delivery_mode || 'online',
      zoomLink: course.zoom_link || '',
      paymentMethods: { ...JSON.parse(JSON.stringify(DEFAULT_PAYMENT_METHODS)), ...(course.payment_methods || {}) },
      scholarshipsEnabled: !!course.scholarships_enabled,
      scholarshipsMax: course.scholarships_max || 0,
      startDate: course.start_date || '',
      endDate: course.end_date || '',
      enrollmentFormEnabled: course.enrollment_form_enabled || false,
      webhookUrl: course.webhook_url || '',
      instructorWhatsapp: course.instructor_whatsapp || '',
      instructorWhatsappMessage: course.instructor_whatsapp_message || '',
    });
    setImagePreview(course.image_url ? (course.image_url.startsWith('/api') ? `${API_BASE}${course.image_url}` : course.image_url) : null);
    setTempImageFile(null);
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const courseData = {
      title: form.title,
      slug: form.slug,
      description: form.description,
      instructor_id: user.id,
      requirements: form.requirements || null,
      hours: form.hours ? parseInt(form.hours) : null,
      image_url: form.imageUrl || null,
      status: form.status,
      is_free: form.isFree,
      price: !form.isFree && form.price ? parseFloat(form.price) : null,
      delivery_mode: form.deliveryMode,
      zoom_link: form.deliveryMode === 'zoom' || form.deliveryMode === 'hybrid' ? (form.zoomLink || null) : null,
      payment_methods: form.paymentMethods,
      scholarships_enabled: !!form.scholarshipsEnabled,
      scholarships_max: form.scholarshipsEnabled ? parseInt(form.scholarshipsMax || 0, 10) : 0,
      start_date: form.startDate || '',
      end_date: form.endDate || '',
      enrollment_form_enabled: form.enrollmentFormEnabled,
      webhook_url: form.webhookUrl || null,
      instructor_whatsapp: form.instructorWhatsapp || null,
      instructor_whatsapp_message: form.instructorWhatsappMessage || null,
    };

    try {
      let courseId;
      if (editingCourse) {
        await updateCourse(editingCourse.id, courseData);
        courseId = editingCourse.id;
        toast.success('Curso actualizado');
      } else {
        const res = await createCourse(courseData);
        courseId = res.data.id;
        toast.success('Curso creado');
      }
      // Upload image if a new file was selected
      if (tempImageFile && courseId) {
        setImageUploading(true);
        try {
          const fd = new FormData();
          fd.append('file', tempImageFile);
          await uploadCourseImage(courseId, fd);
        } catch {
          toast.error('Curso guardado, pero hubo un error al subir la imagen');
        } finally {
          setImageUploading(false);
        }
      }
      setDialogOpen(false);
      resetForm();
      fetchCourses();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al guardar curso');
    }
  };

  const [confirmDelete, setConfirmDelete] = useState(null);

  const handleDelete = async () => {
    if (!confirmDelete) return;
    const courseId = confirmDelete.id;
    setConfirmDelete(null);
    try {
      await deleteCourse(courseId);
      toast.success('Curso eliminado');
      fetchCourses();
    } catch (err) {
      toast.error(err.response?.data?.detail || err.message || 'Error al eliminar curso');
    }
  };

  if (loading) return <div className="p-8">Cargando...</div>;

  return (
    <div className="p-6 md:p-12 bg-secondary min-h-screen" data-testid="manage-courses-page">
      <div className="max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-heading font-bold text-primary mb-2">Gestionar Cursos</h1>
            <p className="text-muted-foreground">Crea y administra cursos, precios y métodos de pago</p>
          </div>
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button data-testid="create-course-btn"><Plus className="w-4 h-4 mr-2" />Nuevo Curso</Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCourse ? 'Editar Curso' : 'Crear Nuevo Curso'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit}>
                <Tabs defaultValue="basic" className="w-full">
                  <TabsList className="w-full mb-4">
                    <TabsTrigger value="basic" className="flex-1">Básico</TabsTrigger>
                    <TabsTrigger value="pricing" className="flex-1">Precio y Pagos</TabsTrigger>
                    <TabsTrigger value="delivery" className="flex-1">Clases / Zoom</TabsTrigger>
                    <TabsTrigger value="form" className="flex-1">Formulario</TabsTrigger>
                  </TabsList>

                  {/* ─── Tab: Básico ─────────────────────────────── */}
                  <TabsContent value="basic" className="space-y-4">
                    <div className="flex items-center justify-between bg-secondary/50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-muted-foreground">Información principal del curso visible en el catálogo público.</p>
                      <HelpIcon title="¿Qué subir en este apartado?" testId="help-basic">
                        <p><strong>Título:</strong> nombre claro y atractivo (40–80 caracteres).</p>
                        <p><strong>Slug:</strong> versión amigable para URL. Ej: <em>fundamentos-ciberseguridad</em>. Sin espacios ni acentos.</p>
                        <p><strong>Descripción:</strong> qué aprenderá el estudiante, en 2–4 párrafos.</p>
                        <p><strong>Requisitos:</strong> conocimientos previos o prerequisitos (opcional).</p>
                        <p><strong>Duración:</strong> total de horas estimadas para completar el curso.</p>
                        <p><strong>Estado:</strong> usa <em>Borrador</em> mientras lo armas y <em>Publicado</em> cuando esté listo para los estudiantes.</p>
                        <p><strong>Imagen de portada:</strong> ver detalle abajo en la sección de imagen.</p>
                      </HelpIcon>
                    </div>

                    <div className="space-y-2">
                      <Label>Título *</Label>
                      <Input value={form.title} onChange={e => { setField('title', e.target.value); if (!editingCourse) setField('slug', e.target.value.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '')); }} required data-testid="course-title-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Slug (URL) *</Label>
                      <Input value={form.slug} onChange={e => setField('slug', e.target.value)} required data-testid="course-slug-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Descripción *</Label>
                      <Textarea value={form.description} onChange={e => setField('description', e.target.value)} required rows={4} data-testid="course-description-input" />
                    </div>
                    <div className="space-y-2">
                      <Label>Requisitos</Label>
                      <Textarea value={form.requirements} onChange={e => setField('requirements', e.target.value)} rows={2} />
                    </div>
                    <div className="grid grid-cols-2 gap-4">
                      <div className="space-y-2">
                        <Label>Duración (horas)</Label>
                        <Input type="number" value={form.hours} onChange={e => setField('hours', e.target.value)} />
                      </div>
                      <div className="space-y-2">
                        <Label>Estado</Label>
                        <Select value={form.status} onValueChange={v => setField('status', v)}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="draft">Borrador</SelectItem>
                            <SelectItem value="published">Publicado</SelectItem>
                            <SelectItem value="archived">Archivado</SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    {/* Image upload */}
                    <div className="space-y-2">
                      <div className="flex items-center justify-between">
                        <Label>Imagen de portada</Label>
                        <HelpIcon title="Imagen de portada del curso" testId="help-cover-image">
                          <p><strong>Tamaño recomendado:</strong> <span className="text-primary font-semibold">1280 × 720 px</span> (relación 16:9).</p>
                          <p><strong>Tamaño mínimo:</strong> 800 × 450 px.</p>
                          <p><strong>Peso máximo:</strong> 2 MB para mejor carga.</p>
                          <p><strong>Formatos:</strong> JPG, PNG, WEBP, GIF.</p>
                          <p><strong>Consejo:</strong> usa imágenes con buen contraste, evita texto pequeño porque se recortará en algunos dispositivos. Las primeras 3 imágenes del catálogo se etiquetan como "Nuevo".</p>
                          <p><strong>Bancos gratuitos:</strong> unsplash.com, pexels.com, pixabay.com.</p>
                        </HelpIcon>
                      </div>
                      <p className="text-xs text-muted-foreground -mt-1">
                        Recomendado <strong>1280 × 720 px</strong> (16:9) — se muestra en el catálogo y en redes sociales al compartir.
                      </p>
                      <div className="flex gap-3 items-start">
                        <div
                          className="flex-1 border-2 border-dashed rounded-xl p-4 text-center cursor-pointer hover:border-primary transition-colors"
                          onClick={() => imageRef.current?.click()}
                        >
                          {imagePreview ? (
                            <img src={imagePreview} alt="preview" className="h-32 mx-auto object-cover rounded-lg" />
                          ) : (
                            <>
                              <Image className="w-8 h-8 mx-auto text-muted-foreground mb-1" />
                              <p className="text-xs text-muted-foreground">Clic para subir imagen</p>
                              <p className="text-[11px] text-muted-foreground/70 mt-0.5">1280 × 720 px · JPG/PNG/WEBP</p>
                            </>
                          )}
                          <input ref={imageRef} type="file" accept="image/*" className="hidden" onChange={handleImagePick} />
                        </div>
                        <div className="flex-1 space-y-1">
                          <p className="text-xs text-muted-foreground">O pega una URL:</p>
                          <Input value={form.imageUrl} onChange={e => { setField('imageUrl', e.target.value); setImagePreview(e.target.value); }} placeholder="https://..." />
                        </div>
                      </div>
                      {imageUploading && <p className="text-xs text-blue-600">Subiendo imagen...</p>}
                    </div>
                  </TabsContent>

                  {/* ─── Tab: Precio y Pagos ─────────────────────── */}
                  <TabsContent value="pricing" className="space-y-5">
                    <div className="flex items-center justify-between bg-secondary/50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-muted-foreground">Define si el curso es gratis o de pago y cómo aceptar el dinero.</p>
                      <HelpIcon title="Precios y métodos de pago" testId="help-pricing">
                        <p><strong>Curso gratis:</strong> el estudiante puede inscribirse directamente sin pago.</p>
                        <p><strong>Precio (USD):</strong> se muestra en la página del curso y en el formulario.</p>
                        <p><strong>Métodos de pago:</strong> activa los que tu organización acepta. Cada uno requiere un dato (link, número, instrucciones).</p>
                        <p><strong>PayPal:</strong> usa <em>paypal.me/tuusuario</em> o un link de pago.</p>
                        <p><strong>SINPE/Nequi/Yappy/Daviplata:</strong> número de teléfono asociado.</p>
                        <p><strong>Transferencia:</strong> escribe banco, cuenta, titular y cualquier referencia.</p>
                        <p><strong>Otro:</strong> usa para Zelle, Zinli, criptomonedas, etc.</p>
                        <p>El estudiante verá tus instrucciones y luego subirá un comprobante en el formulario.</p>
                      </HelpIcon>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                      <div>
                        <p className="font-semibold text-sm">Curso Gratuito</p>
                        <p className="text-xs text-muted-foreground">Sin costo para el estudiante</p>
                      </div>
                      <Switch checked={form.isFree} onCheckedChange={v => setField('isFree', v)} data-testid="course-free-toggle" />
                    </div>

                    {!form.isFree && (
                      <div className="space-y-2">
                        <Label>Precio (USD)</Label>
                        <div className="relative">
                          <DollarSign className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                          <Input type="number" min="0" step="0.01" value={form.price} onChange={e => setField('price', e.target.value)} className="pl-9" placeholder="99.00" data-testid="course-price-input" />
                        </div>
                      </div>
                    )}

                    {!form.isFree && (
                      <div className="space-y-4">
                        <h3 className="font-semibold text-sm flex items-center gap-2">
                          <DollarSign className="w-4 h-4" /> Métodos de Pago Aceptados
                        </h3>

                        {/* PayPal */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">PayPal</Label>
                            <Switch checked={form.paymentMethods.paypal?.enabled} onCheckedChange={v => setPayment('paypal', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.paypal?.enabled && (
                            <Input value={form.paymentMethods.paypal.link} onChange={e => setPayment('paypal', 'link', e.target.value)} placeholder="https://paypal.me/..." />
                          )}
                        </div>

                        {/* SINPE */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">SINPE Móvil (Costa Rica)</Label>
                            <Switch checked={form.paymentMethods.sinpe?.enabled} onCheckedChange={v => setPayment('sinpe', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.sinpe?.enabled && (
                            <div className="grid grid-cols-2 gap-2">
                              <Input value={form.paymentMethods.sinpe.number} onChange={e => setPayment('sinpe', 'number', e.target.value)} placeholder="Número (ej: 8888-8888)" />
                              <Input value={form.paymentMethods.sinpe.name} onChange={e => setPayment('sinpe', 'name', e.target.value)} placeholder="Nombre del titular" />
                            </div>
                          )}
                        </div>

                        {/* Nequi */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">Nequi (Colombia)</Label>
                            <Switch checked={form.paymentMethods.nequi?.enabled} onCheckedChange={v => setPayment('nequi', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.nequi?.enabled && (
                            <Input value={form.paymentMethods.nequi.number} onChange={e => setPayment('nequi', 'number', e.target.value)} placeholder="Número Nequi" />
                          )}
                        </div>

                        {/* Daviplata */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">Daviplata (Colombia)</Label>
                            <Switch checked={form.paymentMethods.daviplata?.enabled} onCheckedChange={v => setPayment('daviplata', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.daviplata?.enabled && (
                            <Input value={form.paymentMethods.daviplata.number} onChange={e => setPayment('daviplata', 'number', e.target.value)} placeholder="Número Daviplata" />
                          )}
                        </div>

                        {/* MercadoPago */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">Mercado Pago</Label>
                            <Switch checked={form.paymentMethods.mercadopago?.enabled} onCheckedChange={v => setPayment('mercadopago', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.mercadopago?.enabled && (
                            <Input value={form.paymentMethods.mercadopago.link} onChange={e => setPayment('mercadopago', 'link', e.target.value)} placeholder="https://mpago.la/..." />
                          )}
                        </div>

                        {/* Yappy */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">Yappy (Panamá)</Label>
                            <Switch checked={form.paymentMethods.yappy?.enabled} onCheckedChange={v => setPayment('yappy', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.yappy?.enabled && (
                            <Input value={form.paymentMethods.yappy.number} onChange={e => setPayment('yappy', 'number', e.target.value)} placeholder="Número Yappy" />
                          )}
                        </div>

                        {/* Bank Transfer */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">Transferencia Bancaria</Label>
                            <Switch checked={form.paymentMethods.bank_transfer?.enabled} onCheckedChange={v => setPayment('bank_transfer', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.bank_transfer?.enabled && (
                            <Textarea value={form.paymentMethods.bank_transfer.details} onChange={e => setPayment('bank_transfer', 'details', e.target.value)} placeholder="Banco, número de cuenta, tipo de cuenta, nombre del titular..." rows={3} />
                          )}
                        </div>

                        {/* Pago Offline */}
                        <div className="border rounded-xl p-4 space-y-3 bg-amber-50/40 border-amber-200">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold flex items-center gap-2">
                              <span className="inline-block w-5 h-5 rounded-full bg-amber-500 text-white text-xs font-bold flex items-center justify-center">$</span>
                              Pago Offline
                            </Label>
                            <Switch
                              checked={form.paymentMethods.offline?.enabled}
                              onCheckedChange={v => setPayment('offline', 'enabled', v)}
                              data-testid="payment-offline-switch"
                            />
                          </div>
                          {form.paymentMethods.offline?.enabled && (
                            <>
                              <Textarea
                                value={form.paymentMethods.offline.details}
                                onChange={e => setPayment('offline', 'details', e.target.value)}
                                placeholder="Ej: Pago en efectivo en nuestra oficina (San José, Costa Rica) lunes a viernes 8:00–17:00. Coordinar visita previa al WhatsApp +506 ..."
                                rows={3}
                                data-testid="payment-offline-details"
                              />
                              <p className="text-xs text-muted-foreground">
                                El estudiante verá estas instrucciones y podrá marcar "Pago Offline" en el formulario.
                                El pago se confirmará manualmente desde el panel de solicitudes.
                              </p>
                            </>
                          )}
                        </div>

                        {/* Other */}
                        <div className="border rounded-xl p-4 space-y-3">
                          <div className="flex items-center justify-between">
                            <Label className="font-semibold">Otro método</Label>
                            <Switch checked={form.paymentMethods.other?.enabled} onCheckedChange={v => setPayment('other', 'enabled', v)} />
                          </div>
                          {form.paymentMethods.other?.enabled && (
                            <div className="space-y-2">
                              <Input value={form.paymentMethods.other.name} onChange={e => setPayment('other', 'name', e.target.value)} placeholder="Nombre del método (ej: Zinli, Zelle...)" />
                              <Textarea value={form.paymentMethods.other.details} onChange={e => setPayment('other', 'details', e.target.value)} placeholder="Instrucciones de pago..." rows={2} />
                            </div>
                          )}
                        </div>

                        {/* Scholarships (Becas) */}
                        <div className="border-2 border-accent/40 bg-accent/5 rounded-xl p-4 space-y-3" data-testid="scholarships-block">
                          <div className="flex items-center justify-between">
                            <div>
                              <Label className="font-semibold flex items-center gap-2">
                                🎓 Becas (cupos sin pago)
                              </Label>
                              <p className="text-xs text-muted-foreground mt-1">
                                Permite que estudiantes se inscriban como "Becados" sin pagar. Define cuántos cupos hay disponibles.
                              </p>
                            </div>
                            <Switch
                              checked={form.scholarshipsEnabled}
                              onCheckedChange={v => setField('scholarshipsEnabled', v)}
                              data-testid="scholarships-toggle"
                            />
                          </div>
                          {form.scholarshipsEnabled && (
                            <div className="space-y-2">
                              <Label className="text-xs">Número de cupos de beca</Label>
                              <Input
                                type="number"
                                min="0"
                                value={form.scholarshipsMax}
                                onChange={e => setField('scholarshipsMax', e.target.value)}
                                placeholder="Ej: 5"
                                data-testid="scholarships-max-input"
                              />
                              <p className="text-xs text-muted-foreground">
                                Cuando se alcance ese número de inscripciones aprobadas o pendientes con beca, la opción se ocultará automáticamente del formulario público.
                              </p>
                            </div>
                          )}
                        </div>
                      </div>
                    )}
                  </TabsContent>

                  {/* ─── Tab: Clases / Zoom ─────────────────────── */}
                  <TabsContent value="delivery" className="space-y-4">
                    <div className="flex items-center justify-between bg-secondary/50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-muted-foreground">Cómo se imparte el curso al estudiante.</p>
                      <HelpIcon title="Modalidades de impartición" testId="help-delivery">
                        <p><strong>Online (grabado):</strong> el estudiante avanza a su ritmo con video y texto.</p>
                        <p><strong>Zoom (en vivo):</strong> clases sincrónicas. Necesitas un link de Zoom.</p>
                        <p><strong>Híbrido:</strong> combina lecciones grabadas + clases en vivo por Zoom.</p>
                        <p><strong>Presencial:</strong> el curso se da en una sede física. Indica detalles en la descripción.</p>
                        <p><strong>Link de Zoom:</strong> sólo lo verán los estudiantes ya inscritos.</p>
                      </HelpIcon>
                    </div>

                    {/* Course dates */}
                    <div className="border rounded-xl p-4 space-y-3 bg-blue-50/40" data-testid="course-dates-block">
                      <Label className="font-semibold flex items-center gap-2">
                        📅 Fechas del curso
                      </Label>
                      <p className="text-xs text-muted-foreground -mt-1">
                        Si configuras una fecha de fin y pasa, el curso se marcará automáticamente como <strong>finalizado</strong> y se bloquearán nuevas inscripciones. Limpia los campos o pon fechas nuevas para reactivarlo.
                      </p>
                      <div className="grid grid-cols-2 gap-3">
                        <div>
                          <Label className="text-xs">Fecha de inicio</Label>
                          <Input
                            type="date"
                            value={form.startDate || ''}
                            onChange={e => setField('startDate', e.target.value)}
                            data-testid="course-start-date"
                          />
                        </div>
                        <div>
                          <Label className="text-xs">Fecha de fin</Label>
                          <Input
                            type="date"
                            value={form.endDate || ''}
                            onChange={e => setField('endDate', e.target.value)}
                            data-testid="course-end-date"
                          />
                        </div>
                      </div>
                      {form.startDate && form.endDate && new Date(form.endDate) < new Date(form.startDate) && (
                        <p className="text-xs text-red-600">⚠️ La fecha de fin es anterior a la de inicio.</p>
                      )}
                      {(form.startDate || form.endDate) && (
                        <button
                          type="button"
                          onClick={() => { setField('startDate', ''); setField('endDate', ''); }}
                          className="text-xs text-blue-700 underline hover:no-underline"
                          data-testid="clear-dates-btn"
                        >
                          Limpiar fechas (curso permanente)
                        </button>
                      )}
                    </div>

                    <div className="space-y-2">
                      <Label>Modalidad del Curso</Label>
                      <Select value={form.deliveryMode} onValueChange={v => setField('deliveryMode', v)}>
                        <SelectTrigger data-testid="course-delivery-mode">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="online">Online (grabado)</SelectItem>
                          <SelectItem value="zoom">Zoom (clases en vivo)</SelectItem>
                          <SelectItem value="hybrid">Híbrido (online + Zoom)</SelectItem>
                          <SelectItem value="in-person">Presencial</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>

                    {(form.deliveryMode === 'zoom' || form.deliveryMode === 'hybrid') && (
                      <div className="space-y-2">
                        <Label className="flex items-center gap-2">
                          <Video className="w-4 h-4 text-blue-600" /> Link de Zoom
                        </Label>
                        <Input value={form.zoomLink} onChange={e => setField('zoomLink', e.target.value)} placeholder="https://zoom.us/j/..." data-testid="course-zoom-link" />
                        <p className="text-xs text-muted-foreground">Solo los estudiantes inscritos podrán ver este enlace.</p>
                      </div>
                    )}

                    <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl text-sm text-blue-800 space-y-1">
                      <p className="font-semibold">¿Cómo obtener el link de Zoom?</p>
                      <p>1. Entra a zoom.us → Reuniones → Programar nueva reunión</p>
                      <p>2. Copia el enlace de "Invitar a participantes"</p>
                      <p>3. Pégalo aquí para que los estudiantes inscritos puedan unirse</p>
                    </div>

                    {/* WhatsApp del instructor */}
                    <div className="space-y-3 border-t pt-4">
                      <div className="flex items-center justify-between">
                        <Label className="flex items-center gap-2 text-base font-semibold">
                          <span className="inline-block w-5 h-5 rounded-full bg-green-500 text-white text-xs font-bold flex items-center justify-center">W</span>
                          Contacto WhatsApp del instructor
                        </Label>
                        <HelpIcon title="WhatsApp del instructor" testId="help-whatsapp">
                          <p>Permite a los estudiantes contactar directamente al instructor por WhatsApp.</p>
                          <p><strong>Formato:</strong> número internacional, ej. <em>+506 8888 8888</em>.</p>
                          <p>Aparecerá un botón verde "Contactar al instructor por WhatsApp" en la página del curso y en el formulario de inscripción.</p>
                          <p>El mensaje predeterminado se rellena automáticamente cuando el estudiante hace clic.</p>
                        </HelpIcon>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Número de WhatsApp (con código de país)</Label>
                        <Input
                          value={form.instructorWhatsapp}
                          onChange={e => setField('instructorWhatsapp', e.target.value)}
                          placeholder="+506 8888 8888"
                          data-testid="course-whatsapp-input"
                        />
                        <p className="text-xs text-muted-foreground">Solo números, espacios y signo "+". Sin guiones ni paréntesis.</p>
                      </div>

                      <div className="space-y-2">
                        <Label className="text-sm">Mensaje predeterminado (opcional)</Label>
                        <Textarea
                          value={form.instructorWhatsappMessage}
                          onChange={e => setField('instructorWhatsappMessage', e.target.value)}
                          rows={2}
                          placeholder="Hola, quiero información sobre el curso..."
                          data-testid="course-whatsapp-message"
                        />
                        <p className="text-xs text-muted-foreground">Si lo dejas vacío, se usará un mensaje genérico con el nombre del curso.</p>
                      </div>
                    </div>
                  </TabsContent>

                  {/* ─── Tab: Formulario de Inscripción ─────────── */}
                  <TabsContent value="form" className="space-y-5">
                    <div className="flex items-center justify-between bg-secondary/50 px-3 py-2 rounded-lg">
                      <p className="text-xs text-muted-foreground">Formulario embebible para que estudiantes externos se inscriban.</p>
                      <HelpIcon title="Formulario de inscripción + Webhook" testId="help-form">
                        <p><strong>¿Para qué sirve?</strong> Genera un enlace público <em>/enroll/COURSE_ID</em> con un formulario completo (datos personales, profesionales y comprobante de pago).</p>
                        <p><strong>Webhook URL:</strong> URL de tu sistema externo (CRM, Make, Zapier, n8n) donde llegará un POST con todos los datos cada vez que alguien envía el formulario.</p>
                        <p><strong>Formato del webhook:</strong> JSON con secciones <em>course</em>, <em>student</em>, <em>professional</em>, <em>payment</em>.</p>
                        <p><strong>Email automático:</strong> aunque no configures webhook, siempre se notifica al admin por email.</p>
                        <p><strong>Compartir:</strong> el link del formulario aparece aquí abajo. También puedes embeberlo con iframe (ver <em>GUIA_INTEGRACION_FORMULARIO.md</em>).</p>
                      </HelpIcon>
                    </div>

                    <div className="flex items-center justify-between p-4 bg-secondary rounded-xl">
                      <div>
                        <p className="font-semibold text-sm">Activar Formulario de Inscripción</p>
                        <p className="text-xs text-muted-foreground">El estudiante llenará un formulario con sus datos y comprobante de pago</p>
                      </div>
                      <Switch checked={form.enrollmentFormEnabled} onCheckedChange={v => setField('enrollmentFormEnabled', v)} data-testid="course-form-toggle" />
                    </div>

                    {form.enrollmentFormEnabled && (
                      <>
                        <div className="space-y-2">
                          <Label className="flex items-center gap-2">
                            <Webhook className="w-4 h-4 text-purple-600" /> Webhook URL (opcional)
                          </Label>
                          <Input value={form.webhookUrl} onChange={e => setField('webhookUrl', e.target.value)} placeholder="https://tu-sistema.com/webhook/inscripciones" data-testid="course-webhook-url" />
                          <p className="text-xs text-muted-foreground">Cuando un estudiante envíe el formulario, se hará un POST a esta URL con los datos.</p>
                        </div>

                        <div className="p-4 bg-purple-50 border border-purple-200 rounded-xl text-sm text-purple-800 space-y-2">
                          <p className="font-semibold flex items-center gap-1"><Link2 className="w-4 h-4" /> Enlace del Formulario Embebible</p>
                          {editingCourse ? (
                            <>
                              <p className="break-all font-mono text-xs bg-white border rounded p-2">
                                {window.location.origin}/enroll/{editingCourse.id}
                              </p>
                              <p className="text-xs">Comparte este enlace o incrústalo en otra web con un iframe.</p>
                            </>
                          ) : (
                            <p className="text-xs">El enlace estará disponible después de crear el curso.</p>
                          )}
                        </div>
                      </>
                    )}
                  </TabsContent>
                </Tabs>

                <div className="flex gap-2 pt-6 border-t mt-4">
                  <Button type="submit" data-testid="save-course-btn" disabled={imageUploading}>
                    {imageUploading ? 'Subiendo imagen...' : (editingCourse ? 'Actualizar' : 'Crear')} Curso
                  </Button>
                  <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancelar</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid grid-cols-1 gap-4">
          {courses.length === 0 ? (
            <Card><CardContent className="py-12 text-center"><p className="text-muted-foreground">No hay cursos creados aún</p></CardContent></Card>
          ) : (
            courses.map((course) => (
              <Card key={course.id} className="hover:shadow-md transition-shadow">
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex gap-4 flex-1">
                      {course.image_url && (
                        <img src={course.image_url.startsWith('/api') ? `${API_BASE}${course.image_url}` : course.image_url}
                          alt={course.title} className="w-24 h-24 object-cover rounded-lg" />
                      )}
                      <div className="flex-1">
                        <div className="flex items-center gap-2 flex-wrap mb-2">
                          <h3 className="text-lg font-semibold">{course.title}</h3>
                          <Badge variant={course.status === 'published' ? 'default' : 'secondary'}>{course.status}</Badge>
                          {course.is_free === false && course.price && (
                            <Badge className="bg-green-100 text-green-700">USD ${Number(course.price).toFixed(2)}</Badge>
                          )}
                          {course.delivery_mode === 'zoom' && <Badge className="bg-blue-100 text-blue-700">Zoom</Badge>}
                          <CourseStateBadge course={course} showActive />
                        </div>
                        <p className="text-sm text-muted-foreground mb-2 line-clamp-2">{course.description}</p>
                        <div className="flex items-center gap-4 text-sm text-muted-foreground">
                          {course.hours && <span>{course.hours}h</span>}
                          <span>Instructor: {course.instructor_name}</span>
                          {course.enrollment_form_enabled && <span className="text-purple-600">Formulario activo</span>}
                        </div>
                      </div>
                    </div>
                    <div className="flex gap-2 ml-2">
                      <ShareCourseButton course={course} />
                      <Link to={`/admin/courses/${course.id}/content`}>
                        <Button size="sm" variant="outline" data-testid={`edit-content-${course.id}`}>
                          <BookOpen className="w-4 h-4" />
                        </Button>
                      </Link>
                      <Button size="sm" variant="outline" onClick={() => handleEdit(course)} data-testid={`edit-course-${course.id}`}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      {['superadmin', 'admin'].includes(user?.role) && (
                        <Button size="sm" variant="destructive" onClick={() => setConfirmDelete(course)} data-testid={`delete-course-${course.id}`}>
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))
          )}
        </div>
      </div>

      {/* Confirm delete dialog */}
      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>¿Eliminar curso?</AlertDialogTitle>
            <AlertDialogDescription>
              Estás por eliminar <strong>{confirmDelete?.title}</strong>. Esto borrará también
              sus módulos, lecciones, inscripciones y solicitudes asociadas. Los artículos vinculados
              se desvincularán pero no se eliminarán. Esta acción no se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-course">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              data-testid="confirm-delete-course"
            >
              Sí, eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default ManageCourses;
